import { css } from 'lit';

/**
 * All styles for native form elements inside <asc-form-wrapper>.
 * Injected into document.adoptedStyleSheets (NOT a shadow root).
 * Every selector is scoped under `asc-form-wrapper` to avoid page bleed.
 *
 * Uses the same --asc-* design tokens as the Lit web components so both
 * renderings stay visually in sync.
 */
export const styles = css`
  asc-form-wrapper {
    display: block;
  }

  /* ── Text inputs ─────────────────────────────────────────────── */

  asc-form-wrapper .form-control:not(textarea) {
    display: block;
    width: 100%;
    box-sizing: border-box;
    min-height: var(--asc-input-height-md, 44px);
    padding: var(--asc-input-padding-y-md, var(--asc-spacing-md))
      var(--asc-input-padding-x, 14px);
    background: var(--asc-input-bg, var(--asc-bg-primary));
    border: var(--asc-input-border-width, var(--asc-border-width)) solid
      var(--asc-input-border-color, var(--asc-border-primary));
    border-radius: var(--asc-input-border-radius, var(--asc-radius-sm));
    box-shadow: var(--asc-input-shadow, 0px 1px 2px 0px var(--asc-shadow-xs));
    font-family: var(--asc-input-font-family, var(--asc-font-family-body));
    font-size: var(--asc-input-font-size-md, var(--asc-font-size-text-md));
    font-weight: var(--asc-input-font-weight, var(--asc-font-weight-regular));
    line-height: var(--asc-line-height-text-md);
    color: var(--asc-input-text-color, var(--asc-text-primary));
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    transition:
      border-color var(--asc-transition-fast),
      box-shadow var(--asc-transition-fast);
  }

  asc-form-wrapper .form-control:not(textarea)::placeholder {
    color: var(--asc-input-text-color-placeholder, var(--asc-text-placeholder));
  }

  asc-form-wrapper .form-control:not(textarea):focus {
    border-color: var(--asc-input-border-color-focus, var(--asc-border-brand));
    box-shadow:
      var(--asc-input-shadow, 0px 1px 2px 0px var(--asc-shadow-xs)),
      0 0 0 3px var(--asc-input-focus-ring, var(--asc-focus-ring));
  }

  asc-form-wrapper .form-control:not(textarea):disabled {
    background: var(--asc-input-bg-disabled, var(--asc-bg-disabled_subtle));
    border-color: var(--asc-input-border-color-disabled, var(--asc-border-disabled));
    color: var(--asc-input-text-color-disabled, var(--asc-text-disabled));
    cursor: not-allowed;
    box-shadow: var(--asc-input-shadow, 0px 1px 2px 0px var(--asc-shadow-xs));
  }

  /* ── Textarea ────────────────────────────────────────────────── */

  asc-form-wrapper textarea.form-control {
    display: block;
    width: 100%;
    box-sizing: border-box;
    min-height: var(--asc-textarea-input-min-height, 96px);
    padding: var(--asc-textarea-input-padding-y, var(--asc-spacing-lg))
      var(--asc-textarea-input-padding-x, 14px);
    background: var(--asc-textarea-input-bg, var(--asc-bg-primary));
    border: var(--asc-textarea-input-border-width, var(--asc-border-width)) solid
      var(--asc-textarea-input-border-color, var(--asc-border-primary));
    border-radius: var(--asc-textarea-input-border-radius, var(--asc-radius-md));
    box-shadow: 0px 1px 2px 0px var(--asc-shadow-xs);
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-textarea-input-font-size, var(--asc-font-size-text-md));
    font-weight: var(--asc-textarea-input-font-weight, var(--asc-font-weight-regular));
    line-height: var(--asc-textarea-input-line-height, var(--asc-line-height-text-md));
    color: var(--asc-textarea-input-text-color, var(--asc-text-primary));
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    resize: vertical;
    transition:
      border-color var(--asc-transition-fast),
      box-shadow var(--asc-transition-fast);
  }

  asc-form-wrapper textarea.form-control::placeholder {
    color: var(--asc-textarea-input-placeholder-color, var(--asc-text-placeholder));
  }

  asc-form-wrapper textarea.form-control:focus {
    border-color: var(--asc-textarea-input-border-color-focus, var(--asc-border-brand));
    box-shadow:
      0px 1px 2px 0px var(--asc-shadow-xs),
      0 0 0 3px var(--asc-focus-ring);
  }

  asc-form-wrapper textarea.form-control:disabled {
    background: var(--asc-textarea-input-bg-disabled, var(--asc-bg-disabled_subtle));
    border-color: var(--asc-textarea-input-border-color-disabled, var(--asc-border-disabled));
    color: var(--asc-textarea-input-text-color-disabled, var(--asc-text-disabled));
    cursor: not-allowed;
    box-shadow: 0px 1px 2px 0px var(--asc-shadow-xs);
  }

  /* ── Select ──────────────────────────────────────────────────── */

  asc-form-wrapper select,
  asc-form-wrapper select.form-select {
    display: block;
    width: 100%;
    box-sizing: border-box;
    min-height: 44px;
    padding:
      var(--asc-select-trigger-padding-y, var(--asc-spacing-md))
      var(--asc-select-trigger-padding-x, var(--asc-spacing-lg));
    padding-inline-end: calc(
      var(--asc-select-trigger-padding-x, var(--asc-spacing-lg)) + 20px
    );
    background-color: var(--asc-select-trigger-bg, var(--asc-bg-primary));
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23667085' stroke-width='1.67' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right var(--asc-select-trigger-padding-x, var(--asc-spacing-lg)) center;
    border: var(--asc-border-width, 1px) solid
      var(--asc-select-trigger-border-color, var(--asc-border-primary));
    border-radius: var(--asc-select-trigger-radius, var(--asc-radius-md));
    box-shadow: var(--asc-select-trigger-shadow, 0px 1px 2px 0px var(--asc-shadow-xs));
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-font-size-text-sm);
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-select-value-color, var(--asc-text-primary));
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    transition:
      border-color var(--asc-transition-fast),
      box-shadow var(--asc-transition-fast);
  }

  asc-form-wrapper select:focus,
  asc-form-wrapper select.form-select:focus {
    border-color: var(--asc-select-trigger-border-color-focus, var(--asc-border-brand));
    box-shadow:
      var(--asc-select-trigger-shadow, 0px 1px 2px 0px var(--asc-shadow-xs)),
      0 0 0 3px var(--asc-focus-ring);
  }

  asc-form-wrapper select:disabled,
  asc-form-wrapper select.form-select:disabled {
    background-color: var(--asc-select-trigger-bg-disabled, var(--asc-bg-disabled_subtle));
    border-color: var(--asc-select-trigger-border-color-disabled, var(--asc-border-disabled));
    color: var(--asc-select-text-disabled, var(--asc-text-disabled));
    cursor: not-allowed;
    pointer-events: none;
  }

  /* ── Checkbox & Radio ────────────────────────────────────────── */

  asc-form-wrapper input[type='checkbox'],
  asc-form-wrapper input[type='radio'] {
    width: var(--asc-checkbox-size-sm, 16px);
    height: var(--asc-checkbox-size-sm, 16px);
    border-radius: var(--asc-checkbox-radius-sm, var(--asc-radius-xs));
    border: 1px solid var(--asc-checkbox-border-color, var(--asc-border-primary));
    background: transparent;
    appearance: none;
    -webkit-appearance: none;
    flex-shrink: 0;
    margin: 0;
    padding: 0;
    cursor: pointer;
    transition: background-color 100ms ease, border-color 100ms ease;
  }

  asc-form-wrapper input[type='radio'] {
    border-radius: var(--asc-checkbox-radio-radius, var(--asc-radius-full));
  }

  asc-form-wrapper input[type='checkbox']:checked,
  asc-form-wrapper input[type='radio']:checked {
    background-color: var(--asc-checkbox-checked-bg, var(--asc-bg-brand-solid));
    border-color: var(--asc-checkbox-checked-bg, var(--asc-bg-brand-solid));
  }

  asc-form-wrapper input[type='checkbox']:checked {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 8' fill='none'%3E%3Cpath d='M1 4L3.5 6.5L9 1' stroke='white' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 75%;
  }

  asc-form-wrapper input[type='radio']:checked {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Ccircle cx='8' cy='8' r='3' fill='white'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 100%;
  }

  asc-form-wrapper input[type='checkbox']:focus,
  asc-form-wrapper input[type='radio']:focus {
    outline: none;
    box-shadow:
      0 0 0 2px var(--asc-checkbox-focus-ring-gap-color, var(--asc-bg-primary)),
      0 0 0 4px var(--asc-checkbox-focus-ring-color, var(--asc-focus-ring));
  }

  asc-form-wrapper input[type='checkbox']:disabled,
  asc-form-wrapper input[type='radio']:disabled {
    background-color: var(--asc-checkbox-disabled-bg, var(--asc-bg-disabled_subtle));
    border-color: var(--asc-checkbox-disabled-border, var(--asc-border-disabled));
    cursor: not-allowed;
  }

  asc-form-wrapper .form-check {
    display: inline-flex;
    align-items: flex-start;
    gap: var(--asc-checkbox-gap, var(--asc-spacing-sm));
    padding: 0;
    cursor: pointer;
  }

  asc-form-wrapper .checkboxlist,
  asc-form-wrapper .radiobuttonlist {
    display: flex;
    flex-direction: column;
    gap: var(--asc-spacing-sm);
  }

  asc-form-wrapper .form-check-label {
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-checkbox-label-font-size-sm, var(--asc-font-size-text-sm));
    font-weight: var(--asc-checkbox-label-font-weight, var(--asc-font-weight-medium));
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-checkbox-label-color, var(--asc-text-secondary));
    margin-bottom: 0;
    cursor: pointer;
  }

  /* ── Buttons (submit / next / previous) ──────────────────────── */

  asc-form-wrapper input[type='submit'].btn-primary,
  asc-form-wrapper input[type='submit'].btn-secondary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-height: var(--asc-spacing-5xl);
    padding:
      var(--asc-button-padding-y-md, 10px)
      var(--asc-button-padding-x-md, 14px);
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-button-font-size-md, var(--asc-font-size-text-sm));
    font-weight: var(--asc-font-weight-semibold);
    line-height: 1;
    border: var(--asc-border-width, 1px) solid transparent;
    border-radius: var(--asc-button-border-radius, var(--asc-radius-sm));
    cursor: pointer;
    white-space: nowrap;
    user-select: none;
    appearance: none;
    -webkit-appearance: none;
    transition:
      background-color var(--asc-transition-fast),
      border-color var(--asc-transition-fast),
      color var(--asc-transition-fast),
      box-shadow var(--asc-transition-fast);
  }

  asc-form-wrapper input[type='submit'].btn-primary {
    background-color: var(--asc-button-primary-bg, var(--asc-bg-brand-solid));
    color: var(--asc-button-primary-fg, var(--asc-text-primary_on-brand));
    box-shadow:
      0 1px 2px 0 var(--asc-shadow-xs),
      inset 0 0 0 2px rgba(255, 255, 255, 0.12);
  }

  asc-form-wrapper input[type='submit'].btn-primary:hover {
    background-color: var(--asc-button-primary-bg-hover, var(--asc-bg-brand-solid_hover));
  }

  asc-form-wrapper input[type='submit'].btn-primary:focus-visible {
    outline: none;
    box-shadow:
      0 1px 2px 0 var(--asc-shadow-xs),
      inset 0 0 0 2px rgba(255, 255, 255, 0.12),
      0 0 0 2px var(--asc-button-focus-ring-separator, white),
      0 0 0 var(--asc-button-focus-ring-width, 4px) var(--asc-button-focus-ring, var(--asc-focus-ring));
  }

  asc-form-wrapper input[type='submit'].btn-primary:disabled {
    background-color: var(--asc-button-disabled-bg, var(--asc-bg-disabled_subtle));
    border-color: var(--asc-button-disabled-border, var(--asc-border-disabled_subtle));
    color: var(--asc-button-disabled-fg, var(--asc-text-disabled));
    box-shadow: none;
    cursor: not-allowed;
  }

  asc-form-wrapper input[type='submit'].btn-secondary {
    background-color: var(--asc-button-secondary-bg, var(--asc-bg-primary));
    border-color: var(--asc-button-secondary-border, var(--asc-border-primary));
    color: var(--asc-button-secondary-fg, var(--asc-text-secondary));
    box-shadow: 0 1px 2px 0 var(--asc-shadow-xs);
  }

  asc-form-wrapper input[type='submit'].btn-secondary:hover {
    background-color: var(--asc-button-secondary-bg-hover, var(--asc-bg-primary_hover));
    color: var(--asc-button-secondary-fg-hover, var(--asc-text-secondary_hover));
  }

  asc-form-wrapper input[type='submit'].btn-secondary:focus-visible {
    outline: none;
    box-shadow:
      0 1px 2px 0 var(--asc-shadow-xs),
      0 0 0 2px var(--asc-button-focus-ring-separator, white),
      0 0 0 var(--asc-button-focus-ring-width, 4px) var(--asc-button-focus-ring, var(--asc-focus-ring));
  }

  asc-form-wrapper input[type='submit'].btn-secondary:disabled {
    background-color: var(--asc-bg-primary);
    border-color: var(--asc-border-disabled_subtle);
    color: var(--asc-fg-disabled_subtle);
    box-shadow: none;
    cursor: not-allowed;
  }

  /* ── Error states ────────────────────────────────────────────── */

  asc-form-wrapper .form-control.input-validation-error:not(textarea),
  asc-form-wrapper .form-control:not(textarea):user-invalid,
  asc-form-wrapper select.input-validation-error,
  asc-form-wrapper select:user-invalid {
    border-color: var(--asc-input-border-color-destructive, var(--asc-border-error));
  }

  asc-form-wrapper .form-control.input-validation-error:not(textarea):focus,
  asc-form-wrapper .form-control:not(textarea):user-invalid:focus,
  asc-form-wrapper select.input-validation-error:focus,
  asc-form-wrapper select:user-invalid:focus {
    border-color: var(--asc-input-border-color-destructive, var(--asc-border-error));
    box-shadow:
      var(--asc-input-shadow, 0px 1px 2px 0px var(--asc-shadow-xs)),
      0 0 0 3px var(--asc-focus-ring-error);
  }

  asc-form-wrapper textarea.input-validation-error,
  asc-form-wrapper textarea:user-invalid {
    border-color: var(
      --asc-textarea-input-border-color-destructive,
      var(--asc-border-error_subtle)
    );
  }

  asc-form-wrapper textarea.input-validation-error:focus,
  asc-form-wrapper textarea:user-invalid:focus {
    border-color: var(
      --asc-textarea-input-border-color-destructive-focus,
      var(--asc-border-error)
    );
    box-shadow:
      0px 1px 2px 0px var(--asc-shadow-xs),
      0 0 0 3px var(--asc-focus-ring-error);
  }

  asc-form-wrapper input[type='checkbox'].input-validation-error,
  asc-form-wrapper input[type='checkbox']:user-invalid,
  asc-form-wrapper input[type='radio'].input-validation-error,
  asc-form-wrapper input[type='radio']:user-invalid {
    border-color: var(--asc-checkbox-error-border, var(--asc-border-error));
  }

  asc-form-wrapper input[type='checkbox'].input-validation-error:focus,
  asc-form-wrapper input[type='checkbox']:user-invalid:focus,
  asc-form-wrapper input[type='radio'].input-validation-error:focus,
  asc-form-wrapper input[type='radio']:user-invalid:focus {
    outline: none;
    border-color: var(--asc-checkbox-error-border, var(--asc-border-error));
    box-shadow:
      0 0 0 2px var(--asc-checkbox-focus-ring-gap-color, var(--asc-bg-primary)),
      0 0 0 4px var(--asc-focus-ring-error);
  }

  /* ── Labels ──────────────────────────────────────────────────── */

  asc-form-wrapper legend {
    color: var(--asc-input-label-color, var(--asc-text-secondary));
  }

  asc-form-wrapper label,
  asc-form-wrapper label.form-label,
  asc-form-wrapper .umbraco-forms-label {
    display: block;
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-input-label-font-size, var(--asc-font-size-text-sm));
    font-weight: var(--asc-font-weight-medium);
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-input-label-color, var(--asc-text-secondary));
    margin-bottom: var(--asc-spacing-sm);
    cursor: default;
  }

  asc-form-wrapper label abbr[title],
  asc-form-wrapper .umbraco-forms-field-required {
    color: var(--asc-text-brand-tertiary);
    text-decoration: none;
  }

  /* ── Hints / tooltips ────────────────────────────────────────── */

  asc-form-wrapper .umbraco-forms-tooltip,
  asc-form-wrapper small,
  asc-form-wrapper .help-block {
    display: block;
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-input-hint-font-size, var(--asc-font-size-text-sm));
    font-weight: var(--asc-font-weight-regular);
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-input-hint-color, var(--asc-text-tertiary));
    margin-top: var(--asc-spacing-sm);
  }

  /* ── Validation errors ───────────────────────────────────────── */

  asc-form-wrapper .field-validation-error,
  asc-form-wrapper .text-danger {
    display: block;
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-input-hint-font-size, var(--asc-font-size-text-sm));
    font-weight: var(--asc-font-weight-regular);
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-input-error-color, var(--asc-text-error-primary));
    margin-top: var(--asc-spacing-sm);
  }

  /* ── Hidden fields ──────────────────────────────────────────── */

  asc-form-wrapper .umbraco-forms-hidden {
    display: none !important;
  }

  /* Screen-reader-only content — visually hidden but accessible to AT */
  asc-form-wrapper .visually-hidden {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
  }

  /* ── Field layout (each Umbraco form field container) ───────── */

  asc-form-wrapper .umbraco-forms-field {
    display: flex;
    flex-direction: column;
    gap: var(--asc-spacing-sm);
    margin-bottom: var(--asc-spacing-xl);
  }

  asc-form-wrapper .umbraco-forms-field label {
    margin-bottom: 0;
  }

  /* ── Navigation ──────────────────────────────────────────────── */

  asc-form-wrapper .umbraco-forms-navigation {
    margin-top: var(--asc-spacing-xl);
  }

  asc-form-wrapper .umbraco-forms-navigation .text-end {
    display: flex;
    justify-content: flex-end;
    gap: var(--asc-spacing-md);
  }
`;
